import { useRef, useState } from "react";
import { useFlangeTables, useReloadFlangeTables, useUploadFlangeTables } from "../../api/admin";
import { formatDateTime } from "../../api/dates";
import { Card, StatusChip } from "../../components/ui";
import AdminLayout from "./AdminLayout";

/** The flange drilling tables: which version is in force and how to load the
 * next. Only one version is ever in force, so a measured flange is matched
 * against one table and a later import cannot change an old match. */
export function FlangeTablesPage() {
  const view = useFlangeTables();
  const upload = useUploadFlangeTables();
  const reload = useReloadFlangeTables();
  const [version, setVersion] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const data = view.data;
  return (
    <AdminLayout title="Flange tables" sub="The drillings a measured flange is matched against">
      {view.isError ? <p className="notice bad">The flange tables could not be loaded: {view.error.message}</p> : null}
      {view.isPending ? <div className="table-card empty-state">Loading flange tables…</div> : null}
      {data?.loaded ? <p className="notice ok">{data.loaded}</p> : null}
      {data ? (
        data.active ? (
          <p className="notice ok">
            In force: <b>{data.active.source_version}</b> ({data.active.rows} rows).
          </p>
        ) : (
          <p className="notice warn">No flange tables loaded. Section 2 of the checklist cannot match a flange until they are.</p>
        )
      ) : null}
      {data && data.versions.length > 0 ? (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Version</th>
                <th className="num">Rows</th>
                <th>Loaded</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.versions.map((v) => (
                <tr key={v.source_version}>
                  <td className="mono">{v.source_version}</td>
                  <td className="num">{v.rows}</td>
                  <td>{formatDateTime(v.imported_at)}</td>
                  <td>
                    <StatusChip tone={v.active ? "ok" : "quiet"}>{v.active ? "In force" : "Retired"}</StatusChip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {data ? (
        <Card title="Load a new version" sub={`Sheet ${data.sheet_name}; configured version ${data.configured_version} in ${data.configured_workbook}.`}>
          <div className="toolbar">
            <label className="field">
              New version name
              <input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="e.g. checklist-2026-10" />
            </label>
            <input
              ref={input}
              type="file"
              accept=".xlsx"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  upload.mutate({ version: version.trim(), file });
                }
                e.target.value = "";
              }}
            />
            <button
              type="button"
              className="btn btn-primary toolbar-end"
              disabled={upload.isPending || version.trim() === ""}
              onClick={() => input.current?.click()}
            >
              Upload the workbook (up to {data.max_upload_mb} MB)
            </button>
            <button type="button" className="btn" disabled={reload.isPending} onClick={() => reload.mutate()}>
              Load the configured workbook
            </button>
          </div>
          {upload.isError ? <p className="notice bad">{upload.error.message}</p> : null}
          {reload.isError ? <p className="notice bad">{reload.error.message}</p> : null}
        </Card>
      ) : null}
    </AdminLayout>
  );
}
